import { useEffect, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Progress,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core'
import { wallClockToInstant } from '@racedash/shared'
import type { SessionType, TimetableExtraction } from '@racedash/shared'
import { functions } from '../lib/firebase'
import { createSession } from '../data/catalog'
import type { Series } from '../data/catalog'

const SESSION_TYPES: SessionType[] = ['practice', 'qualifying', 'race', 'other']

/**
 * How long the client waits for the extraction.
 *
 * A callable's default is 70 seconds, which is NOT the function's own timeout —
 * and reading a timetable takes about 35 seconds of model time, doubled when a
 * failed check triggers a repair pass, on top of a cold start. The default
 * therefore expires mid-call and surfaces as "deadline exceeded" while the
 * function is still working. Matched to the function's own 300s ceiling so the
 * server decides when to give up, not the client.
 */
const CALL_TIMEOUT_MS = 300_000

/** What the extraction usually costs, so the wait can be described honestly. */
const TYPICAL_SECONDS = 45

/**
 * One reviewable row. Separate from the extractor's output because everything
 * here is editable: what comes back is a reading of a PDF, and the person who
 * knows whether it is right is the one looking at both.
 */
interface Row {
  include: boolean
  name: string
  seriesId: string | null
  type: SessionType
  date: string
  startTime: string
  minutes: string
  /** Kept verbatim so the reviewer can check a row against the page. */
  sourceText: string
  original: string
  provisional: boolean
  notes: string | null
  /** Evidence of a misreading — shown as an error. */
  warnings: string[]
  /** A disagreement for the reviewer to settle, such as a day outside the
   *  event's own dates. Shown, but not as a fault. */
  advisories: string[]
}

type Step =
  | { kind: 'url' }
  | { kind: 'reading' }
  | { kind: 'failed'; message: string }
  | { kind: 'review'; result: TimetableExtraction; rows: Row[] }
  | { kind: 'saving'; done: number; total: number }

/**
 * Reads a published timetable PDF into sessions.
 *
 * Entering a weekend by hand is a dozen forms of the same shape, and the
 * organiser has already published the whole thing — as a PDF laid out for
 * people, in whatever language and layout they favour. So the document goes to
 * Claude and comes back as a proposal.
 *
 * The whole import is one modal carried through: paste the URL, watch it read,
 * check what it found, confirm. It takes the best part of a minute and ends in
 * a decision, so it is not something to wander away from — leaving mid-read
 * abandons work already paid for, and editing the session list underneath a
 * pending proposal is editing against a list about to change.
 *
 * Nothing is written until the proposal is confirmed.
 */
export default function TimetableImport({
  opened,
  onClose,
  eventId,
  timezone,
  series,
  onImported,
}: {
  opened: boolean
  onClose: () => void
  eventId: string
  timezone: string
  series: Series[]
  onImported: (count: number) => void
}) {
  const [url, setUrl] = useState('')
  const [step, setStep] = useState<Step>({ kind: 'url' })

  const busy = step.kind === 'reading' || step.kind === 'saving'

  // Each opening starts at the URL. A proposal left over from last time would
  // be about a document nobody asked for again.
  useEffect(() => {
    if (opened) setStep({ kind: 'url' })
  }, [opened])

  async function read(e: React.FormEvent) {
    e.preventDefault()
    if (!url.trim() || busy) return
    setStep({ kind: 'reading' })
    try {
      const call = httpsCallable<{ url: string; eventId: string }, TimetableExtraction>(
        functions,
        'extractTimetable',
        { timeout: CALL_TIMEOUT_MS },
      )
      const res = await call({ url: url.trim(), eventId })
      setStep({ kind: 'review', result: res.data, rows: toRows(res.data, series) })
    } catch (err) {
      setStep({ kind: 'failed', message: describe(err) })
    }
  }

  async function importSelected() {
    if (step.kind !== 'review') return
    const chosen = step.rows.filter((r) => r.include)
    if (chosen.length === 0) return
    setStep({ kind: 'saving', done: 0, total: chosen.length })
    try {
      let done = 0
      for (const r of chosen) {
        const start = wallClockToInstant(`${r.date}T${r.startTime}`, timezone)
        await createSession({
          eventId,
          seriesId: r.seriesId!,
          name: r.name.trim(),
          type: r.type,
          scheduledStart: start,
          scheduledEnd: new Date(start.getTime() + Number(r.minutes) * 60_000),
        })
        done++
        setStep({ kind: 'saving', done, total: chosen.length })
      }
      setUrl('')
      onImported(done)
      onClose()
    } catch (err) {
      setStep({ kind: 'failed', message: describe(err) })
    }
  }

  function update(i: number, patch: Partial<Row>) {
    setStep((prev) =>
      prev.kind === 'review'
        ? { ...prev, rows: prev.rows.map((r, n) => (n === i ? { ...r, ...patch } : r)) }
        : prev,
    )
  }

  const review = step.kind === 'review' ? step : null
  const ready = review?.rows.filter((r) => r.include) ?? []
  const incomplete = ready.filter((r) => !r.date || !r.startTime || !r.minutes || !r.seriesId)

  return (
    <Modal
      opened={opened}
      onClose={() => !busy && onClose()}
      size={review ? '90rem' : 'lg'}
      // While a call is in flight the work is already paid for and cannot be
      // cancelled server-side, so dismissing would only hide it.
      closeOnEscape={!busy}
      closeOnClickOutside={!busy}
      withCloseButton={!busy}
      title={
        review ? (
          <div>
            <Text fw={600}>
              {review.result.proposal.documentTitle ?? 'Timetable'}
              {review.result.proposal.circuit ? ` · ${review.result.proposal.circuit}` : ''}
            </Text>
            <Text size="sm" c="dimmed">
              {review.rows.length} rows read · {ready.length} selected
              {review.result.repaired ? ' · corrected on a second pass' : ''}
            </Text>
          </div>
        ) : step.kind === 'failed' ? (
          'That did not work'
        ) : step.kind === 'saving' ? (
          'Adding sessions'
        ) : step.kind === 'reading' ? (
          'Reading the timetable'
        ) : (
          'Import a timetable'
        )
      }
    >
      {step.kind === 'url' && (
        <form onSubmit={(e) => void read(e)}>
          <Stack gap="md">
            <Text c="dimmed" size="sm">
              Reads the organiser's published PDF and proposes sessions from it. Nothing is saved
              until you confirm — every row is shown beside the text it came from.
            </Text>
            <TextInput
              label="Timetable PDF URL"
              value={url}
              onChange={(e) => setUrl(e.currentTarget.value)}
              placeholder="https://www.example.com/timing-2026.pdf"
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)', fontSize: 12 } }}
              data-autofocus
              required
            />
            <Group justify="flex-end">
              <Button variant="default" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={!url.trim()}>
                Next
              </Button>
            </Group>
          </Stack>
        </form>
      )}

      {step.kind === 'reading' && <Reading />}

      {step.kind === 'failed' && (
        <Stack gap="md">
          <Alert color="red">{step.message}</Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStep({ kind: 'url' })}>
              Back
            </Button>
            <Button onClick={onClose}>Close</Button>
          </Group>
        </Stack>
      )}

      {step.kind === 'saving' && (
        <Stack gap="xs">
          <Text c="dimmed" size="sm">
            {step.done} of {step.total} added.
          </Text>
          <Progress value={(step.done / step.total) * 100} />
        </Stack>
      )}

      {review && (
        <Stack gap="sm">
          {review.result.proposal.documentNotes && (
            <Alert color="gray" variant="light">
              {review.result.proposal.documentNotes}
            </Alert>
          )}
          {review.result.warnings
            .filter((w) => w.row === null)
            .map((w, i) => (
              <Alert color="red" key={i}>
                {w.message}
              </Alert>
            ))}

          <Table.ScrollContainer minWidth={980}>
            <Table verticalSpacing="xs" horizontalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36} />
                  <Table.Th>From the document</Table.Th>
                  <Table.Th w={150}>Name</Table.Th>
                  <Table.Th w={140}>Series</Table.Th>
                  <Table.Th w={125}>Type</Table.Th>
                  <Table.Th w={130}>Day</Table.Th>
                  <Table.Th w={95}>Start</Table.Th>
                  <Table.Th w={85}>Mins</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {review.rows.map((r, i) => (
                  <Table.Tr key={i} opacity={r.include ? 1 : 0.55}>
                    <Table.Td>
                      <Checkbox
                        checked={r.include}
                        onChange={(e) => update(i, { include: e.currentTarget.checked })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" size="xs">
                        {r.sourceText}
                      </Text>
                      <Group gap={6} mt={3}>
                        {r.provisional && (
                          <Badge size="xs" color="yellow" variant="light">
                            provisional
                          </Badge>
                        )}
                        {r.original !== r.name && (
                          <Text size="xs" c="dimmed">
                            printed as “{r.original}”
                          </Text>
                        )}
                      </Group>
                      {r.notes && (
                        <Text size="xs" c="dimmed" mt={2}>
                          {r.notes}
                        </Text>
                      )}
                      {r.advisories.map((w, n) => (
                        <Text size="xs" c="dimmed" mt={2} key={n}>
                          {w}
                        </Text>
                      ))}
                      {r.warnings.map((w, n) => (
                        <Text size="xs" c="red" mt={2} key={n}>
                          {w}
                        </Text>
                      ))}
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        value={r.name}
                        onChange={(e) => update(i, { name: e.currentTarget.value })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Select
                        size="xs"
                        data={series.map((s) => ({ value: s.id, label: s.name }))}
                        value={r.seriesId}
                        onChange={(v) => update(i, { seriesId: v })}
                        placeholder="—"
                      />
                    </Table.Td>
                    <Table.Td>
                      <Select
                        size="xs"
                        data={SESSION_TYPES}
                        value={r.type}
                        onChange={(v) => update(i, { type: (v as SessionType) ?? 'other' })}
                        allowDeselect={false}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        type="date"
                        value={r.date}
                        onChange={(e) => update(i, { date: e.currentTarget.value })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        type="time"
                        value={r.startTime}
                        onChange={(e) => update(i, { startTime: e.currentTarget.value })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs"
                        min={1}
                        value={r.minutes}
                        onChange={(v) => update(i, { minutes: String(v) })}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          <Group justify="flex-end">
            {incomplete.length > 0 && (
              <Text size="xs" c="dimmed" mr="auto">
                {incomplete.length} selected {incomplete.length === 1 ? 'row needs' : 'rows need'} a
                series, date, start or length.
              </Text>
            )}
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={ready.length === 0 || incomplete.length > 0}
              onClick={() => void importSelected()}
            >
              Add {ready.length} {ready.length === 1 ? 'session' : 'sessions'}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  )
}

/**
 * The wait.
 *
 * An elapsed count and an indeterminate bar rather than a percentage: the work
 * is a single API call that reports nothing until it is done, and a bar that
 * advanced on a timer would be inventing knowledge we do not have.
 */
function Reading() {
  const [seconds, setSeconds] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [])

  return (
    <Stack gap="xs">
      <Text c="dimmed" size="sm">
        Fetching the PDF, reading it, and checking the result against this event's dates.
      </Text>
      <Progress value={100} animated />
      <Text size="xs" c="dimmed" role="status">
        {seconds}s elapsed.{' '}
        {seconds < TYPICAL_SECONDS
          ? `This usually takes about ${TYPICAL_SECONDS} seconds.`
          : 'Longer than usual — a document that fails a check is read a second time.'}
      </Text>
    </Stack>
  )
}

/** Callable errors carry a code that explains far more than the message. */
function describe(err: unknown): string {
  const e = err as { code?: string; message?: string }
  if (e?.code === 'functions/deadline-exceeded') {
    return 'The request timed out before the timetable came back. It may simply be a long document — try again.'
  }
  if (e?.code === 'functions/permission-denied') {
    return 'Your admin session was not accepted. Sign out and back in, then try again.'
  }
  return e?.message ?? 'Could not read that timetable.'
}

/**
 * Turns the extraction into editable rows.
 *
 * Only complete, warning-free track sessions start selected, and only the three
 * types capture actually cares about — "other" means on track but none of them,
 * a grid formation or a parade, which is exactly the case a person should
 * decide on. The rest stay listed but unticked: a timetable is mostly
 * scrutineering and briefings, and the reviewer needs to see the whole document
 * to trust that nothing was dropped.
 */
function toRows(result: TimetableExtraction, series: Series[]): Row[] {
  return result.proposal.sessions.map((s, i) => {
    const forRow = result.warnings.filter((w) => w.row === i)
    const warnings = forRow.filter((w) => !w.advisory).map((w) => w.message)
    const advisories = forRow.filter((w) => w.advisory).map((w) => w.message)
    const minutes = lengthOf(s.startTime, s.endTime, s.durationMinutes)
    const seriesId = matchSeries(s.seriesLabel, series)
    const complete = Boolean(s.date && s.startTime && minutes && seriesId && s.type)
    const captured = s.type === 'practice' || s.type === 'qualifying' || s.type === 'race'
    return {
      include: s.onTrack && captured && complete && warnings.length === 0,
      name: s.name,
      seriesId,
      type: s.type ?? 'other',
      date: s.date ?? '',
      startTime: s.startTime ?? '',
      minutes: minutes ? String(minutes) : '',
      sourceText: s.sourceText,
      original: s.nameOriginal,
      provisional: s.provisional,
      notes: s.notes,
      warnings,
      advisories,
    }
  })
}

/** A length in minutes, from whichever of the two forms the document used. */
function lengthOf(start: string | null, end: string | null, duration: number | null): number | null {
  if (duration && duration > 0) return duration
  if (!start || !end) return null
  const a = toMinutes(start)
  const b = toMinutes(end)
  if (a === null || b === null || b <= a) return null
  return b - a
}

function toMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim())
  if (!m) return null
  return Number(m[1]) * 60 + Number(m[2])
}

/**
 * Maps a printed series label onto one of ours.
 *
 * Only an exact match after normalising, never a fuzzy one. Attaching a session
 * to the wrong championship is worse than leaving the field empty, and an empty
 * field is visible in review while a confident mismatch is not.
 */
function matchSeries(label: string | null, series: Series[]): string | null {
  if (!label) return null
  const key = normalise(label)
  const hit = series.find(
    (s) =>
      normalise(s.name) === key ||
      // A timetable prints the championship the way the paddock says it, which
      // is often the vendor's string rather than our display name — and those
      // are already recorded for matching the live stream.
      Object.values(s.vendorNames ?? {}).some((names) =>
        (names ?? []).some((n) => normalise(n) === key),
      ),
  )
  return hit?.id ?? null
}

/** Case, accents and punctuation removed. Decomposing first means "Générale"
 *  and "Generale" reduce alike, since the combining marks NFD leaves behind are
 *  not alphanumeric and fall to the same filter. */
function normalise(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '')
}
